import { makeAutoObservable, runInAction } from "mobx";
import type { RecommendedUser } from "@hive/sdk";
import { hive } from "../lib/apiClient";
import { loadRecommendedCreators } from "../lib/discoverRecommendations";

/** Lives only as long as one viewer's empty Following page. */
export class CreatorSuggestionsStore {
	items: RecommendedUser[] = [];
	loading = true;
	error = false;
	busy = new Set<string>();
	changed = false;
	private generation = 0;

	constructor(private readonly viewerId: string, private readonly isCurrentViewer: () => boolean) {
		makeAutoObservable(this, {}, { autoBind: true });
	}

	cancel() { this.generation++; }

	async load() {
		const generation = ++this.generation;
		const current = () => generation === this.generation && this.isCurrentViewer();
		if (!current()) return;
		this.loading = true;
		this.error = false;
		try {
			const items = await loadRecommendedCreators(current);
			if (current()) runInAction(() => { this.items = items.filter((user) => user.id !== this.viewerId); });
		} catch {
			if (current()) runInAction(() => { this.error = true; });
		} finally {
			if (current()) runInAction(() => { this.loading = false; });
		}
	}

	async toggle(user: RecommendedUser): Promise<"ok" | "failed" | "ignored"> {
		if (!this.isCurrentViewer() || this.busy.has(user.id)) return "ignored";
		const generation = this.generation;
		const current = () => generation === this.generation && this.isCurrentViewer();
		const wasFollowing = user.viewer!.isFollowing;
		this.busy.add(user.id);
		try {
			if (wasFollowing) await hive.users.unfollow(user.handle);
			else await hive.users.follow(user.handle);
			if (!current()) return "ignored";
			runInAction(() => {
				user.viewer = { isFollowing: !wasFollowing };
				user.stats = { followersCount: Math.max(0, user.stats!.followersCount + (wasFollowing ? -1 : 1)) };
				this.changed = true;
			});
			return "ok";
		} catch {
			return current() ? "failed" : "ignored";
		} finally {
			if (current()) runInAction(() => { this.busy.delete(user.id); });
		}
	}
}
